import { useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';

import type { IWorkerQC, IWorkerProject } from '@/api/worker';

interface WorkerDetailModalProps {
  selectedQC: IWorkerQC | null;
  selectedProject: IWorkerProject | null;
  period: string;
  onClose: () => void;
}

type DisplayTestRun = {
  test_run_id: string;
  test_run_title: string;
  statuses: Record<string, number>;
};

const ALL_STATUSES = [
  'PASSED',
  'FAILED',
  'BLOCKED',
  'UNTESTED',
  'RETEST',
  'NOT_APPLICABLE',
  'IN_PROGRESS',
  'HOLD',
] as const;

const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const getStatusClass = (status: string) => {
  switch (status.toUpperCase()) {
  case 'PASSED':
    return 'bg-emerald-200 text-emerald-950 border-emerald-400 font-bold';
  case 'FAILED':
    return 'bg-rose-200 text-rose-950 border-rose-400 font-bold';
  case 'BLOCKED':
    return 'bg-amber-200 text-amber-950 border-amber-400 font-bold';
  case 'UNTESTED':
    return 'bg-slate-300 text-slate-950 border-slate-400 font-bold';
  case 'RETEST':
    return 'bg-yellow-200 text-yellow-950 border-yellow-400 font-bold';
  case 'NOT_APPLICABLE':
    return 'bg-purple-200 text-purple-950 border-purple-400 font-bold';
  case 'IN_PROGRESS':
    return 'bg-sky-200 text-sky-950 border-sky-400 font-bold';
  case 'HOLD':
    return 'bg-pink-200 text-pink-950 border-pink-400 font-bold';
  default:
    return 'bg-cyan-200 text-cyan-950 border-cyan-400 font-bold';
  }
};

const webBaseUrl = window.location.origin;

const getProjectUrl = (projectId: string) =>
  `${webBaseUrl}/project/${projectId}`;

const getTestRunUrl = (projectId: string, testRunId: string) =>
  `${webBaseUrl}/project/${projectId}/test-run/${testRunId}`;

const getProjectIssueUrl = (projectId: string) =>
  `${webBaseUrl}/project/${projectId}/test-issue`;

export default function WorkerDetailModal({
  selectedQC,
  selectedProject,
  period,
  onClose,
}: WorkerDetailModalProps) {
  const visible = Boolean(selectedQC || selectedProject);
  const [search, setSearch] = useState('');

  const title = selectedQC
    ? `Detail QC - ${selectedQC.full_name}`
    : `Detail Project - ${selectedProject?.project_title ?? ''}`;

  const filteredProjects = useMemo(() => {
    if (!selectedQC) {
      return [];
    }

    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return selectedQC.projects;
    }

    return selectedQC.projects.filter(project =>
      project.project_title.toLowerCase().includes(keyword),
    );
  }, [selectedQC, search]);

  const getDisplayTestRuns = (
    testRuns: IWorkerQC['projects'][number]['test_runs'],
  ): DisplayTestRun[] => {
    const grouped = new Map<string, DisplayTestRun>();

    testRuns.forEach(testRun => {
      const existing = grouped.get(testRun.test_run_id);

      if (existing) {
        existing.statuses[testRun.status] =
          (existing.statuses[testRun.status] ?? 0) + 1;

        return;
      }

      grouped.set(testRun.test_run_id, {
        test_run_id: testRun.test_run_id,
        test_run_title: testRun.test_run_title,
        statuses: {
          [testRun.status]: 1,
        },
      });
    });

    return Array.from(grouped.values());
  };

  const projectStatuses = selectedProject
    ? Object.keys(selectedProject.test_runs).sort((a, b) => a.localeCompare(b))
    : [];

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={title}
      modal
      className="w-[95vw] max-w-6xl font-sans"
    >
      <div className="space-y-6 text-slate-900">
        <div className="flex items-center justify-between border-b border-slate-300 pb-3">
          <p className="text-sm font-bold text-slate-900">
            Periode Laporan:{' '}
            <span className="text-blue-700 font-extrabold">{period}</span>
          </p>
        </div>

        {/* ================= KONDISI 1: DETAIL QC / ADMIN ================= */}
        {selectedQC && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-200 p-5 rounded-xl border border-slate-400 shadow-sm">
              <div>
                <h3 className="text-base font-bold tracking-wide text-blue-950">
                  Project Assigned ({filteredProjects.length})
                </h3>
                <p className="mt-0.5 text-xs font-bold text-slate-800">
                  Daftar lengkap Test Run dan Create Issue penugasan project.
                </p>
              </div>

              <input
                type="text"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Cari project..."
                className="w-72 rounded-lg border border-slate-400 bg-white px-3.5 py-2 text-xs font-bold text-slate-950 outline-none focus:border-blue-700 focus:ring-2 focus:ring-blue-700/20 shadow-xs"
              />
            </div>

            <div className="space-y-6 max-h-[68vh] overflow-y-auto pr-2">
              {filteredProjects.map(project => {
                const displayTestRuns = getDisplayTestRuns(project.test_runs);
                const projectIssues = selectedQC.issues.filter(
                  issue => issue.project_id === project.project_id,
                );
                const totalProjectTC = project.test_runs.length;

                return (
                  <div
                    key={project.project_id}
                    className="rounded-xl border-2 border-slate-300 bg-white shadow-sm overflow-hidden transition-all duration-200 hover:border-slate-400 hover:shadow-md"
                  >
                    {/* HEADER PROJECT */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-100/90 px-5 py-3.5 border-b-2 border-slate-300">
                      <a
                        href={getProjectUrl(project.project_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-4 py-2 rounded-lg bg-blue-100 text-blue-900 font-extrabold text-sm tracking-wide hover:bg-blue-200 hover:text-blue-950 transition-colors truncate max-w-full sm:max-w-xl shadow-xs border border-blue-200/80"
                        title={project.project_title}
                      >
                        {project.project_title}
                      </a>

                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex items-center rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-300 shadow-2xs">
                          Total TC:{' '}
                          <span className="ml-1.5 text-blue-700 font-extrabold">
                            {totalProjectTC}
                          </span>
                        </span>

                        <a
                          href={getProjectIssueUrl(project.project_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-rose-100 px-3.5 py-1.5 text-xs font-bold text-rose-800 transition-colors shadow-2xs hover:bg-rose-200 active:bg-rose-300"
                          title="Klik untuk melihat daftar issue di project ini"
                        >
                          <span>Issue</span>
                          <span
                            data-testid="project-issue-count"
                            className="rounded-full bg-rose-200 px-2 py-0.5 text-[11px] font-extrabold leading-none text-rose-900"
                          >
                            {projectIssues.length}
                          </span>
                        </a>
                      </div>
                    </div>

                    {/* KONTEN BAWAH */}
                    <div className="p-5 space-y-4 bg-white">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-600">
                            Daftar Test Run
                          </h4>
                        </div>

                        {displayTestRuns.length > 0 ? (
                          <div className="overflow-x-auto rounded-lg border-2 border-slate-300 shadow-2xs">
                            <table className="w-full min-w-max text-xs text-left">
                              <thead className="bg-slate-100 text-slate-700 font-bold border-b-2 border-slate-300">
                                <tr>
                                  <th className="sticky left-0 z-10 bg-slate-100 px-4 py-3 text-left border-r-2 border-slate-300 font-extrabold">
                                    Test Run Title
                                  </th>
                                  {ALL_STATUSES.map(status => (
                                    <th
                                      key={status}
                                      className="px-3.5 py-3 text-center border-r border-slate-300 font-bold uppercase tracking-wider text-[11px]"
                                    >
                                      {formatStatus(status)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-slate-200 text-slate-800">
                                {displayTestRuns.map(testRun => {
                                  const rowTotalTC = Object.values(
                                    testRun.statuses,
                                  ).reduce((a, b) => a + b, 0);

                                  return (
                                    <tr
                                      key={testRun.test_run_id}
                                      className="hover:bg-slate-50 transition-colors group"
                                    >
                                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 transition-colors px-4 py-3 border-r-2 border-slate-300">
                                        <a
                                          href={getTestRunUrl(
                                            project.project_id,
                                            testRun.test_run_id,
                                          )}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-bold text-slate-900 hover:text-blue-600 hover:underline block max-w-[340px] truncate text-xs"
                                          title={testRun.test_run_title}
                                        >
                                          {testRun.test_run_title}
                                        </a>
                                        <span className="text-[11px] font-medium text-slate-500">
                                          Total TC: {rowTotalTC}
                                        </span>
                                      </td>

                                      {ALL_STATUSES.map(status => {
                                        const count =
                                          testRun.statuses[status] ?? 0;

                                        return (
                                          <td
                                            key={status}
                                            className="px-3 py-2.5 text-center border-r border-slate-200 last:border-r-0"
                                          >
                                            <span
                                              className={`inline-flex min-w-[36px] items-center justify-center rounded-md px-2.5 py-1 text-xs font-bold transition-all ${
                                                count > 0
                                                  ? getStatusClass(status)
                                                  : 'bg-slate-100 text-slate-500 border border-slate-300 font-bold'
                                              }`}
                                            >
                                              {count}
                                            </span>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center text-xs font-medium text-slate-500 bg-slate-50">
                            Tidak ada Test Run yang ter-assign.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredProjects.length === 0 && (
                <div className="rounded-xl border border-slate-200 p-6 text-center text-xs font-normal text-slate-500 bg-slate-50/50">
                  Tidak ada data project assigned pada periode ini.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================= KONDISI 2: DETAIL PROJECT ================= */}
        {selectedProject && (
          <div className="space-y-6">
            <div className="overflow-auto rounded-xl border border-slate-300 bg-white shadow-md">
              <table className="w-full min-w-max text-sm">
                <thead className="bg-blue-100/80 text-blue-900 font-semibold border-b border-blue-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold">
                      Project
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold">
                      Total Test Run
                    </th>
                    {projectStatuses.map(status => (
                      <th
                        key={status}
                        className="px-4 py-3 text-center text-xs font-semibold"
                      >
                        {formatStatus(status)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-semibold bg-rose-100 text-rose-900">
                      Total Issue
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-slate-100 transition-colors">
                    <td className="px-5 py-4 text-xs font-extrabold">
                      <a
                        href={getProjectUrl(selectedProject.project_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-800 hover:underline font-extrabold text-sm"
                      >
                        {selectedProject.project_title}
                      </a>
                    </td>

                    <td className="px-4 py-4 text-center text-xs font-extrabold text-slate-950">
                      {selectedProject.total_test_runs}
                    </td>

                    {projectStatuses.map(status => (
                      <td key={status} className="px-3.5 py-4 text-center">
                        <span
                          className={`inline-flex min-w-10 justify-center rounded-md border px-2.5 py-1 text-xs ${getStatusClass(
                            status,
                          )}`}
                        >
                          {selectedProject.test_runs[status] ?? 0}
                        </span>
                      </td>
                    ))}

                    <td className="px-4 py-4 text-center">
                      {selectedProject.total_issues > 0 ? (
                        <a
                          href={getProjectIssueUrl(selectedProject.project_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex min-w-10 items-center justify-center gap-1 rounded-md border border-rose-400 bg-rose-100 px-3 py-1 text-xs font-extrabold text-rose-950 transition shadow-2xs hover:bg-rose-200"
                        >
                          <span>{selectedProject.total_issues}</span>
                        </a>
                      ) : (
                        <span className="inline-flex min-w-10 justify-center rounded-md border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                          0
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="mb-3 text-base font-bold tracking-wide text-blue-950/90">
                Daftar Issue ({selectedProject.issues.length})
              </h3>

              {selectedProject.issues.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-md divide-y divide-slate-300">
                  {selectedProject.issues.map(issue => (
                    <div
                      key={issue.issue_id}
                      className="px-5 py-4 hover:bg-slate-100 transition-colors flex items-center justify-between gap-4"
                    >
                      <p className="text-sm font-medium leading-5 text-slate-800">
                        {issue.issue_title}
                      </p>
                      <span className="text-xs font-bold text-blue-800 shrink-0">
                        Dibuat oleh: {issue.created_by_name}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 p-6 text-center text-xs font-normal text-slate-500 bg-slate-50/50">
                  Tidak ada Issue pada Project ini.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
